/**
 * Modal de configuração para o node Adicionar Fase
 * Permite selecionar categoria e fase do Kanban
 * 
 * RELEASE: 1.0.0 - 2024-12-26 - Novo componente
 */

import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FlowNode, AssignPhaseConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { HelpCircle, Loader2, LayoutGrid } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';

// Schema de validação
const assignPhaseSchema = z.object({
  category_id: z.string().uuid('ID da categoria inválido'),
  category_name: z.string().trim().min(1, 'Nome da categoria é obrigatório'),
  phase_id: z.string().uuid('ID da fase inválido'),
  phase_name: z.string().trim().min(1, 'Nome da fase é obrigatório'),
});

interface ConfigureAssignPhaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: AssignPhaseConfig) => void;
}

interface Category {
  id: string;
  name: string;
}

interface Phase {
  id: string;
  name: string;
  color: string | null;
}

/**
 * Função helper para buscar fases por categoria
 * Usa type assertion para evitar erro TS2589 de inferência excessiva do Supabase
 */
async function fetchPhasesByCategory(categoryId: string): Promise<Phase[]> {
  // Type assertion para evitar inferência excessiva de tipos
  const client = supabase as any;
  const { data, error } = await client
    .from('kanban_phases')
    .select('id, name, color')
    .eq('category_id', categoryId)
    // Nota: kanban_phases não possui coluna is_active (diferente de kanban_categories)
    .order('position');
  
  if (error) throw error;
  return (data || []) as Phase[];
}

/**
 * Dialog de configuração do node Adicionar Fase
 */
export function ConfigureAssignPhaseDialog({ 
  open, 
  onOpenChange, 
  node, 
  onSave 
}: ConfigureAssignPhaseDialogProps) {
  const { currentTenant } = useAuth();
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  
  // Estados para categorias e fases
  const [categories, setCategories] = useState<Category[]>([]);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPhases, setLoadingPhases] = useState(false);
  
  // Estados de seleção
  const [categoryId, setCategoryId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  
  // Estado do dialog de ajuda
  const [showHelp, setShowHelp] = useState(false);

  /**
   * Carrega categorias do Kanban ao abrir o modal
   */
  useEffect(() => {
    if (open && currentTenant?.id) {
      loadCategories();
    }
  }, [open, currentTenant?.id]);

  /**
   * Inicializa valores quando node é passado
   */
  useEffect(() => {
    if (node) {
      const config = node.data.config as AssignPhaseConfig | undefined;
      setCategoryId(config?.category_id || '');
      setPhaseId(config?.phase_id || '');
      
      // Se já tem categoria configurada, carrega as fases dela
      if (config?.category_id) {
        loadPhases(config.category_id);
      }
    }
  }, [node]);

  /**
   * Carrega lista de categorias do Kanban do tenant
   */
  const loadCategories = async () => {
    if (!currentTenant?.id) return;
    
    setLoadingCategories(true);
    try {
      const { data, error } = await supabase
        .from('kanban_categories')
        .select('id, name')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('position');

      if (error) {
        toast.error('Erro ao carregar categorias');
        console.error('[ConfigureAssignPhaseDialog] Erro ao carregar categorias:', error);
        return;
      }

      setCategories(data || []);
    } finally {
      setLoadingCategories(false);
    }
  };

  /**
   * Carrega fases de uma categoria específica
   * Usa função helper para evitar erro TS2589 de inferência excessiva
   */
  const loadPhases = async (catId: string) => {
    if (!catId) {
      setPhases([]);
      return;
    }
    
    setLoadingPhases(true);
    try {
      const phasesData = await fetchPhasesByCategory(catId);
      setPhases(phasesData);
    } catch (err) {
      toast.error('Erro ao carregar fases');
      console.error('[ConfigureAssignPhaseDialog] Erro ao carregar fases:', err);
    } finally {
      setLoadingPhases(false);
    }
  };

  /**
   * Quando categoria muda, carrega as fases correspondentes
   */
  const handleCategoryChange = (newCategoryId: string) => {
    setCategoryId(newCategoryId);
    setPhaseId(''); // Limpa fase selecionada
    loadPhases(newCategoryId);
  };

  /**
   * Salva a configuração do node
   */
  const handleSave = () => {
    if (!node) return;

    // Busca nomes das seleções
    const selectedCategory = categories.find(c => c.id === categoryId);
    const selectedPhase = phases.find(p => p.id === phaseId);

    if (!selectedCategory) {
      toast.error('Selecione uma categoria válida');
      return;
    }

    if (!selectedPhase) {
      toast.error('Selecione uma fase válida');
      return;
    }

    const config: AssignPhaseConfig = {
      category_id: categoryId,
      category_name: selectedCategory.name,
      phase_id: phaseId,
      phase_name: selectedPhase.name,
    };

    // Valida com Zod
    const parseResult = assignPhaseSchema.safeParse(config);
    if (!parseResult.success) {
      const firstError = parseResult.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as any);
    toast.success('Configuração salva com sucesso');
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-teal-500/10 text-teal-600 p-2 rounded">
                  <LayoutGrid className="h-5 w-5" />
                </div>
                <DialogTitle>Configurar Adicionar Fase</DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHelp(true)}
                title="Ver ajuda"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo de ID do Node */}
            {node && <NodeIdField ref={nicknameRef} nodeId={node.id} defaultNickname={node.data.config?.nickname} />}

            {/* Seleção de Categoria */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select 
                value={categoryId} 
                onValueChange={handleCategoryChange}
                disabled={loadingCategories}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    loadingCategories ? "Carregando..." : "Selecione uma categoria"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {categories.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      Nenhuma categoria cadastrada.
                      <br />
                      <span className="text-xs">
                        Crie categorias em Configurações → Kanban
                      </span>
                    </div>
                  ) : (
                    categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Escolha a categoria do Kanban para filtrar as fases
              </p>
            </div>

            {/* Seleção de Fase */}
            <div className="space-y-2">
              <Label>Fase *</Label>
              <Select 
                value={phaseId} 
                onValueChange={setPhaseId}
                disabled={!categoryId || loadingPhases}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !categoryId 
                      ? "Selecione uma categoria primeiro" 
                      : loadingPhases 
                        ? "Carregando..." 
                        : "Selecione uma fase"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {phases.length === 0 && categoryId ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      Nenhuma fase nesta categoria.
                    </div>
                  ) : (
                    phases.map((phase) => (
                      <SelectItem key={phase.id} value={phase.id}>
                        <div className="flex items-center gap-2">
                          {phase.color && (
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: phase.color }}
                            />
                          )}
                          {phase.name}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                A conversa será movida para esta fase no Kanban
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!categoryId || !phaseId}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda */}
      <NodeHelpDialog
        open={showHelp}
        onOpenChange={setShowHelp}
        nodeType="assign_phase"
      />
    </>
  );
}
